import * as React from 'react'

import { XIcon } from 'lucide-react'
import mapboxgl, { MapMouseEvent } from 'mapbox-gl'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IProjectAddressInput } from '~/client/graph'
import { SaveLocationsDocument } from '~/client/graph/operations/generated/Locations.generated'
import { LocationsByProjectIdDocument } from '~/client/graph/operations/generated/LocationType.generated'
import StruxhubMapboxAddressInput from '~/client/src/desktop/components/StruxhubDesktopInputs/StruxhubMapboxAddressInput/StruxhubMapboxAddressInput'
import desktopRoutes from '~/client/src/desktop/constants/desktopRoutes'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DesktopCommonStore from '~/client/src/desktop/stores/ui/DesktopCommon.store'
import { Loader } from '~/client/src/shared/components/Loader'
import { mapboxFeatureToAddress } from '~/client/src/shared/components/MapBoxViewer/mapboxAddress'
import { getAddressFromCoords } from '~/client/src/shared/components/MapBoxViewer/mapboxGeocoding'
import { MapBoxViewer } from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer'
import MapBoxViewerStore from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import MapBoxViewerAdapter from '~/client/src/shared/components/MapControls/MapBoxViewerAdapter'
import { MapControls } from '~/client/src/shared/components/MapControls/MapControls'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectLocation, {
  IProjectLocationDto,
} from '~/client/src/shared/models/LocationObjects/ProjectLocation'
import {
  isLocationOfKind,
  LocationKind,
} from '~/client/src/shared/resources/domain/locationKind'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import GlobeViewSetupStore from '~/client/src/shared/stores/GlobeViewSetup.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import ProjectSetUpPageStore from '../../ProjectSetUpPage.store'
import ProjectSetUpPage from '../../ProjectSetUpPages'
import { getMapBoxPlaces } from '../ProjectWorkspaceSetUp/components/MapBoxView'
import ProjectSettingsStore from '../ProjectWorkspaceSetUp/ProjectSettings.store'
import ProjectCreationStore from './ProjectCreation.store'

import './ProjectCreation.scss'

interface IProps {
  projectSetUpPageStore: ProjectSetUpPageStore
  eventsStore?: DesktopEventStore
  sitemapsStore?: SitemapsStore
  basemapsStore?: BasemapsStore
  locationAttributesStore?: LocationAttributesStore
  sitemapItemsStore?: SitemapItemsStore
  common?: DesktopCommonStore
  userProjectsStore?: UserProjectsStore
  projectsStore?: ProjectsStore
  graphExecutorStore?: GraphExecutorStore
  projectDateStore?: ProjectDateStore
  globeViewsStore?: GlobeViewsStore
  tagsStore?: TagsStore
  tilesetsStore?: TilesetsStore
}

const regExp = /^[+-]?([0-9]*[.])?[0-9]+$/

const formatTooltip = 'Follow the next format: longitude,latitude'
const createNewProject = 'Create new project'
const projectAddress = 'Project address'
const longitudeLatitude = 'Longitude-Latitude'
const savingProject = 'Saving Project'

const notUniqueCodeMessage = (code: string) =>
  `The project with the code [${code}] already exists. Try another value`

const DEFAULT_ZOOM = 6
const ZOOM_ON_ADDRESS = 16
const defaultCoordinates = '0, 0'

@inject(
  'projectsStore',
  'eventsStore',
  'sitemapsStore',
  'basemapsStore',
  'locationAttributesStore',
  'sitemapItemsStore',
  'common',
  'userProjectsStore',
  'graphExecutorStore',
  'projectDateStore',
  'globeViewsStore',
  'tagsStore',
  'tilesetsStore',
)
@observer
export default class ProjectCreation extends React.Component<IProps> {
  @observable private coordinates: string = defaultCoordinates
  public readonly globeViewSetupStore: GlobeViewSetupStore
  private readonly store: ProjectCreationStore
  private eventsStore: DesktopEventStore
  private readonly mapBoxViewerStore: MapBoxViewerStore
  private readonly mapControlsAdapter: MapBoxViewerAdapter
  // The element the Fullscreen control sends fullscreen; without it that button does nothing.
  private readonly mapContainerRef = React.createRef<HTMLDivElement>()

  @observable private latitude: number = 0
  @observable private longitude: number = 0
  private readonly projectSettingsStore: ProjectSettingsStore

  public constructor(props: IProps) {
    super(props)

    this.store = new ProjectCreationStore(props.graphExecutorStore)

    this.globeViewSetupStore = new GlobeViewSetupStore(
      props.eventsStore,
      props.globeViewsStore,
      props.userProjectsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
      props.tagsStore,
    )

    this.mapBoxViewerStore = new MapBoxViewerStore(
      props.sitemapsStore,
      props.tilesetsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
      props.eventsStore.appState,
      this.globeViewSetupStore,
      props.basemapsStore,
      props.eventsStore,
    )
    this.mapControlsAdapter = new MapBoxViewerAdapter(this.mapBoxViewerStore)

    this.projectSettingsStore = new ProjectSettingsStore(
      props.projectsStore,
      props.eventsStore,
      props.projectSetUpPageStore,
      props.graphExecutorStore,
      props.projectDateStore,
    )

    this.projectSettingsStore.setDefaultProjectWorkingHours()
    this.projectSettingsStore.projectAddress =
      this.props.eventsStore.appState.getDefaultProjectAddress()
    const { bearing, zoom, pitch, center } =
      props.eventsStore.appState.projectAddress
    const { latitude, longitude } = props.eventsStore.appState

    const lat = center?.lat || latitude
    const lng = center?.lng || longitude
    // Before MapBoxViewer mounts: that is where the opening pose is read.
    this.mapBoxViewerStore.setViewport({
      latitude: lat,
      longitude: lng,
      zoom: zoom || DEFAULT_ZOOM,
      pitch: pitch || 0,
      bearing,
      padding: null,
    })

    this.latitude = lat
    this.longitude = lng
    this.coordinates = `${this.longitude},${this.latitude}`
    this.eventsStore = props.eventsStore
    makeObservable(this)
  }

  public UNSAFE_componentWillMount(): void {
    this.projectSettingsStore.enableNewProjectSelection()
  }

  public async componentDidMount() {
    const item = await getAddressFromCoords(this.latitude, this.longitude)
    this.mapBoxOnAddressValueChanged(item)
  }

  public render(): JSX.Element {
    if (this.isLoading) {
      return <Loader hint={Localization.translator.loading} />
    }

    return (
      <div className="row full-width full-height project-creation">
        {this.isSavingProject && (
          <>
            <Loader
              className="loader-container absolute full-width full-height unclickable-element col x-center y-center text large white"
              hint={savingProject}
            />
            <div className="absolute full-width full-height unclickable-element text white opacity-background" />
          </>
        )}
        {this.renderContent()}
        {this.renderRightTable()}
      </div>
    )
  }

  private renderContent(): JSX.Element {
    return (
      <div
        className={classList({
          'full-height': true,
          'unclickable-element': this.isSavingProject,
        })}
      >
        <div
          ref={this.mapContainerRef}
          className="map-content relative map-controls-root"
        >
          <MapControls.Provider
            store={this.mapControlsAdapter}
            bare
            containerRef={this.mapContainerRef}
          >
            <MapBoxViewer
              store={this.mapBoxViewerStore}
              onAddressChanged={this.onAddressChanged}
              shouldShowProjectMarker={true}
              latitude={this.latitude}
              longitude={this.longitude}
              updateLngLat={this.updateLngLat}
              language={Localization.state.currentLanguage}
              mapEvents={{
                onMapDblClick: this.onDblClick,
              }}
            />
            <MapControls.Chrome />
          </MapControls.Provider>
        </div>
      </div>
    )
  }

  private renderRightTable(): JSX.Element {
    const { isProjectCodeBeingChecked } = this.store

    return (
      <div className="full-height col side-table bl-palette-grey pa10">
        <div className="row pb20 pt10">
          <XIcon
            className="no-grow pointer"
            size={22}
            onClick={this.closeProjectCreation}
          />
          <div className="text size22 center bold">{createNewProject}</div>
        </div>
        {this.renderLocationRightTable()}
        <div className="col y-end">
          <Button
            variant="default"
            onClick={this.onButtonClick}
            disabled={
              !(!this.isButtonDisabled && !isProjectCodeBeingChecked) ||
              isProjectCodeBeingChecked
            }
          >
            {isProjectCodeBeingChecked ? (
              <Spinner size="xs" />
            ) : (
              <>{Localization.translator.submit_verb}</>
            )}
          </Button>
        </div>
      </div>
    )
  }

  private renderLocationRightTable(): JSX.Element {
    const {
      projectDetailsDescriptions: { projectCodeError, projectNameError },
      projectName,
      projectCode,
    } = Localization.translator
    const {
      projectAddressSuggestions,
      projectName: activeProjectName,
      projectCode: activeProjectCode,
      isProjectErrorMessageVisible,
      isProjectCodeErrorMessageVisible,
    } = this.projectSettingsStore
    const { address } = this.activeProjectAddress

    return (
      <div className="col y-start no-grow mb20">
        <StruxhubInput
          id={projectName}
          label={projectName}
          isRequired={true}
          isValid={!isProjectErrorMessageVisible}
          value={activeProjectName}
          onChange={this.onProjectNameChange}
          onValueReset={this.onProjectNameReset}
          validationMessage={isProjectErrorMessageVisible && projectNameError}
        />
        <StruxhubInput
          id={projectCode}
          label={projectCode}
          isRequired={true}
          isValid={!isProjectCodeErrorMessageVisible}
          value={activeProjectCode}
          onChange={this.onProjectCodeChange}
          onValueReset={this.onProjectCodeReset}
          validationMessage={
            isProjectCodeErrorMessageVisible && projectCodeError
          }
        />
        <div className="col">
          <StruxhubMapboxAddressInput
            label={projectAddress}
            isRequired={true}
            fullAddressLabel={this.projectAddressString}
            items={projectAddressSuggestions}
            onItemSelect={this.mapBoxOnAddressValueChanged}
            onQueryChange={this.mapBoxOnQueryChange}
            value={address}
          />
          <div className="row">
            <TooltipWrapper
              className="coordinates-wrapper"
              content={formatTooltip}
            >
              <StruxhubInput
                id="latLng"
                label={longitudeLatitude}
                isRequired={true}
                value={this.coordinates}
                onChange={this.updateCoordinates}
                onBlur={this.setCoordinates}
              />
            </TooltipWrapper>
          </div>
        </div>
      </div>
    )
  }

  private closeProjectCreation = (): void => {
    this.props.projectSetUpPageStore.navigateTo(
      ProjectSetUpPage.PROJECT_DETAILS,
    )
  }

  private onCreateProjectClick = async (): Promise<void> => {
    this.eventsStore.appState.loading.set(e.SAVE_PROJECT, true)
    this.props.globeViewsStore.clearList()
    await this.projectSettingsStore.submitForm(this.saveGlobe)
  }

  private onButtonClick = async (): Promise<void> => {
    const { projectCode } = this.projectSettingsStore

    try {
      // eslint-disable-next-line no-var
      var isUnique = await this.store.checkProjectCodeUniqueness(
        this.projectSettingsStore.projectCode,
      )
    } catch {
      return alert(
        Localization.translator.somethingWentWrongDuringAPIInteraction,
      )
    }

    if (isUnique) {
      this.onCreateProjectClick()
    } else {
      alert(notUniqueCodeMessage(projectCode))
      this.projectSettingsStore.resetProjectCode()
    }
  }

  private redirectToMapSetup = async (): Promise<void> => {
    runInAction(() => {
      this.eventsStore.appState.loading.set(e.SAVE_PROJECT, false)
    })
    this.props.common._displayView(desktopRoutes.MAP_SETUP)
  }

  private saveGlobe = async (projectId: string): Promise<void> => {
    const { globeCorners } = this.mapBoxViewerStore

    const { viewport } = this.mapBoxViewerStore
    const center = { lat: viewport.latitude, lng: viewport.longitude }

    const bounds = {
      ne: { lat: globeCorners[0].latitude, lng: globeCorners[0].longitude },
      sw: { lat: globeCorners[3].latitude, lng: globeCorners[3].longitude },
    }

    const globeId = await this.globeViewSetupStore.createNewGlobeView(
      null,
      bounds,
      center,
      viewport.zoom,
      0,
      viewport.bearing,
      viewport.pitch,
      globeCorners,
      projectId,
    )

    this.globeViewSetupStore.assignGlobeToAllApps(globeId, projectId)
    const newProjectLocations = (
      await this.props.graphExecutorStore.query(LocationsByProjectIdDocument, {
        projectId,
      })
    ).data

    // Found by KIND, not by the enum. ProjectInitializationService now seeds the site root
    // as a plain Location carrying Data["type"], so it reports LocationType.Location and the
    // old enum test matched nothing - fromDto then threw on an undefined dto and creation
    // died here. isLocationOfKind dual-reads, so a legacy project still resolves.
    const projectLocationDto = newProjectLocations.locations.data.find(
      location => isLocationOfKind(location, LocationKind.ProjectLocation),
    ) as IProjectLocationDto

    if (!projectLocationDto) {
      throw new Error('Default Project location not found for the new project')
    }

    const newProjectLocation = ProjectLocation.fromDto(projectLocationDto)

    runInAction(() => {
      newProjectLocation.assignedGlobes.push(globeId)
    })

    await this.props.graphExecutorStore.mutate(SaveLocationsDocument, {
      locations: [newProjectLocation.getDto()],
    })

    runInAction(() => {
      this.eventsStore.appState.loading.set(e.SAVE_PROJECT, false)
    })

    this.redirectToMapSetup()
  }

  private updateCoordinates = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.coordinates = event.target.value
  }

  private setCoordinates = async () => {
    const [lng, lat] = this.coordinates.trim().replace(/\s/g, '').split(',')

    if (!lng.match(regExp) || !lat.match(regExp)) {
      runInAction(() => {
        this.coordinates = `${this.longitude},${this.latitude}`
      })
      return
    }

    runInAction(() => {
      this.longitude = Number(lng)
      this.latitude = Number(lat)
    })

    const item = await getAddressFromCoords(this.latitude, this.longitude)
    runInAction(() => {
      this.mapBoxOnAddressValueChanged(item)
    })
  }

  private mapBoxOnQueryChange = async (query: string): Promise<void> => {
    const data: GeoJSON.FeatureCollection = await getMapBoxPlaces(
      query,
      mapboxgl.accessToken,
      [this.longitude, this.latitude],
    )

    this.projectSettingsStore.setProjectAddressSuggestions(
      data.features ? (data.features as IMapBoxFeature[]) : [],
    )
  }

  @action
  private mapBoxOnAddressValueChanged = (item: IMapBoxFeature) => {
    if (!item) {
      return
    }

    const addr = mapboxFeatureToAddress(item)
    this.latitude = addr.center.lat
    this.longitude = addr.center.lng

    this.mapBoxViewerStore.jumpToViewport({
      latitude: addr.center.lat,
      longitude: addr.center.lng,
      zoom: ZOOM_ON_ADDRESS,
      bearing: addr.bearing,
    })

    if (!addr.zoom) {
      addr.zoom = ZOOM_ON_ADDRESS
      addr.pitch = this.mapBoxViewerStore.viewport.pitch
    }

    this.projectSettingsStore.setProjectAddress(addr)
    this.coordinates = `${this.longitude}, ${this.latitude}`
  }

  @action.bound
  private onDblClick(e: MapMouseEvent): void {
    this.updateLngLat(e.lngLat.lat, e.lngLat.lng)
  }

  @action
  private updateLngLat = (latitude: number, longitude: number) => {
    this.longitude = longitude
    this.latitude = latitude
    this.coordinates = `${this.longitude}, ${this.latitude}`
  }

  @action.bound
  private onAddressChanged({
    address,
    country,
    city,
    state,
    zipcode,
  }: IProjectAddressInput) {
    const { globeCorners } = this.mapBoxViewerStore

    const bounds = {
      ne: { lat: globeCorners[0].latitude, lng: globeCorners[0].longitude },
      sw: { lat: globeCorners[3].latitude, lng: globeCorners[3].longitude },
    }

    this.projectSettingsStore.projectAddress.bounds = bounds
    this.projectSettingsStore.projectAddress.center.lat = this.latitude
    this.projectSettingsStore.projectAddress.center.lng = this.longitude
    this.projectSettingsStore.projectAddress.address = address
    this.projectSettingsStore.projectAddress.country = country
    this.projectSettingsStore.projectAddress.city = city
    this.projectSettingsStore.projectAddress.state = state
    this.projectSettingsStore.projectAddress.zipcode = zipcode
  }

  private onProjectNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { value: projectName = EMPTY_STRING } = event.target

    this.projectSettingsStore.setProjectName(projectName)
    this.projectSettingsStore.setProjectCode(projectName)
  }

  private onProjectNameReset = (): void => {
    this.projectSettingsStore.setProjectName(EMPTY_STRING)
  }

  private onProjectCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.projectSettingsStore.setProjectCode(event.target.value)
  }

  private onProjectCodeReset = (): void => {
    this.projectSettingsStore.setProjectCode(EMPTY_STRING)
  }

  private get projectAddressString(): string {
    const { address, city, state, zipcode, country } = this.activeProjectAddress

    return `${address} ${city} ${state} ${zipcode} ${country}`
  }

  private get activeProjectAddress(): IProjectAddressInput {
    const { projectAddress: activeProjectAddress } = this.projectSettingsStore

    return activeProjectAddress || ({ projectId: null } as IProjectAddressInput)
  }

  @computed
  private get isLoading(): boolean {
    const { loading } = this.eventsStore.appState
    return loading.get(e.ACTIVATE_PROJECT)
  }

  @computed
  private get isSavingProject(): boolean {
    const { loading } = this.eventsStore.appState

    return (
      loading.get(e.SAVE_PROJECT) ||
      loading.get(e.SAVE_SITEMAP) ||
      loading.get(e.SAVE_GLOBE_VIEW) ||
      loading.get(e.SAVE_BASEMAP)
    )
  }

  private get isButtonDisabled(): boolean {
    const {
      projectName: activeProjectName,
      projectCode,
      isProjectErrorMessageVisible,
      isProjectCodeErrorMessageVisible,
    } = this.projectSettingsStore

    return (
      isProjectErrorMessageVisible ||
      !activeProjectName ||
      isProjectCodeErrorMessageVisible ||
      !projectCode ||
      !this.projectAddressString.trim()
    )
  }
}
